/**
 * WorkStoppagesDataTable
 *
 * "Suivi des arrêts de travail" — sub-view 4 of CollabsView.
 *
 * The columns are the besoin's own list, in its order: the V-PLANNING identity of the
 * salarié (N° SS, entité, site, statut, ancienneté, sortie — all resolved server-side as of
 * the first day of the arrêt), then the arrêt itself, then what the CPAM and the prévoyance
 * have actually paid. Everything here comes from `/v1/workStoppages/getAll`; nothing is
 * derived in the browser, so the grid and the export cannot disagree.
 *
 * The rows are leaves: each one is a `planning` entry whose leave type is flagged
 * `is_work_stoppage`. Declaring an arrêt attaches follow-up data to that leave, it never
 * creates a second record of the absence.
 */
import { useState, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { Chip, IconButton, Stack, Tooltip, FormControlLabel, Switch } from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useSelector } from "react-redux";
import { Localize } from "../../app/lang/langs";
import styles from "./CollabsView.module.css";
import { renderDateCell, usePinnedActionsColumn, singleSelectFilterOperator } from "./collabsTableUtils";
import { REIMBURSEMENT_STATUSES } from "../../components/workStoppages/workStoppagesSlice";
import {
  StatusChip, WorkStoppagePresetFilters, applyWorkStoppagePreset,
} from "../../components/workStoppages/WorkStoppageStatus";

/** Empty state — shown both before anything is declared and when a filter matches nothing. */
function NoRowsOverlay() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(0,0,0,0.5)" }}>
      {Localize("WORK_STOPPAGES_EMPTY")}
    </div>
  );
}

/** Money as compta reads it — two decimals, euro, French grouping. */
const formatAmount = (value) =>
  value === null || typeof value === "undefined" || value === ""
    ? ""
    : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(value));

/** A yes/no column that stays legible when the value is NULL (= not decided yet). */
const renderBoolCell = (params) =>
  params.value === null || typeof params.value === "undefined"
    ? ""
    : Localize(params.value ? "YES" : "NO");

/**
 * @param {Object} props
 * @param {Object} props.apiRef - MUI useGridApiRef() ref
 * @param {Array} props.data - The work stoppages list to display
 * @param {string} props.scrollHeight - CSS scroll height for the table
 * @param {boolean} props.canEdit - whether workStoppages:edit is held (shows edit/delete)
 * @param {boolean} props.onlyUnpaid - the "awaiting CPAM payment" filter state
 * @param {Function} props.onOnlyUnpaidChange - toggles that filter (re-queries the API)
 * @param {boolean} props.onlyMine - the "only my stoppages" filter state
 * @param {Function} props.onOnlyMineChange - toggles that filter (re-queries the API)
 * @param {Function} props.onViewDetails - opens the details drawer for a row
 * @param {Function} props.onOpenPlanning - opens the planning on the arrêt's first day
 * @param {Function} props.onEdit - opens the edit form for a row
 * @param {Function} props.onDelete - asks to delete a row
 * @param {boolean} [props.compact] - drop the card's own top offset and trim its padding, for a
 *        page whose wrapper already clears the fixed top bar
 */
export default function WorkStoppagesDataTable({
  apiRef,
  data,
  scrollHeight,
  canEdit,
  onlyUnpaid,
  onOnlyUnpaidChange,
  onlyMine,
  onOnlyMineChange,
  onViewDetails,
  onOpenPlanning,
  onEdit,
  onDelete,
  compact,
}) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [globalSearch, setGlobalSearch] = useState("");
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  // Predefined filter, applied to the rows before the grid's own filters so the two combine.
  const [preset, setPreset] = useState("ALL");
  const rows = useMemo(() => applyWorkStoppagePreset(data, preset), [data, preset]);
  const lang = useSelector((state) => state.settings.lang);
  const dataGridLocale = lang === "fr" ? frFR : enUS;

  usePinnedActionsColumn(apiRef);

  const columns = useMemo(() => {
    // One column per organisme, each filterable by status — "everything the CPAM still owes"
    // is a column filter on PENDING.
    const reimbursementColumn = (field, headerKey) => ({
      field, headerName: Localize(headerKey, lang), minWidth: 184,
      sortable: true, filterable: true,
      filterOperators: [singleSelectFilterOperator(REIMBURSEMENT_STATUSES.map((status) => ({
        value: status, label: Localize(`WORK_STOPPAGE_REIMBURSEMENT_${status}`, lang),
      })))],
      renderCell: (params) => <StatusChip status={params.value} />,
    });
    return [
    { field: "id", headerName: Localize("ID", lang), minWidth: 88, sortable: true },
    { field: "num_ss", headerName: Localize("WORK_STOPPAGE_NUM_SS", lang), minWidth: 160, sortable: true, filterable: true },
    { field: "entite", headerName: Localize("WORK_STOPPAGE_ENTITY", lang), minWidth: 140, sortable: true, filterable: true },
    { field: "site", headerName: Localize("WORK_STOPPAGE_SITE", lang), minWidth: 140, sortable: true, filterable: true },
    { field: "prenom", headerName: Localize("Name", lang), minWidth: 144, sortable: true, filterable: true },
    { field: "nom", headerName: Localize("Family Name", lang), minWidth: 144, sortable: true, filterable: true },
    // Today's team, next to the historical site above: who to call about the arrêt now.
    { field: "current_team_name", headerName: Localize("WORK_STOPPAGE_CURRENT_TEAM", lang), minWidth: 160, sortable: true, filterable: true },
    { field: "statut", headerName: Localize("WORK_STOPPAGE_EMPLOYEE_STATUS", lang), minWidth: 128, sortable: true, filterable: true },
    {
      field: "date_anciennete", headerName: Localize("WORK_STOPPAGE_SENIORITY_DATE", lang), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "date_sortie", headerName: Localize("WORK_STOPPAGE_EXIT_DATE", lang), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "motif", headerName: Localize("WORK_STOPPAGE_MOTIF", lang), minWidth: 220, sortable: true, filterable: true,
      // Référentiel labels run to paragraphs of legal prose: two lines in the cell, the whole
      // label on hover.
      renderCell: (params) => (params.value ? (
        <Tooltip title={params.value} placement="bottom-start" enterDelay={300}>
          <span
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              whiteSpace: "normal",
              lineHeight: 1.35,
              cursor: "default",
            }}
          >
            {params.value}
          </span>
        </Tooltip>
      ) : ""),
    },
    {
      field: "subrogation", headerName: Localize("WORK_STOPPAGE_SUBROGATION", lang), minWidth: 128,
      sortable: true, filterable: true, renderCell: renderBoolCell,
    },
    {
      field: "salary_maintenance", headerName: Localize("WORK_STOPPAGE_SALARY_MAINTENANCE", lang), minWidth: 168,
      sortable: true, filterable: true, renderCell: renderBoolCell,
    },
    {
      field: "received_at", headerName: Localize("WORK_STOPPAGE_RECEIVED_AT", lang), minWidth: 152,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "start_date", headerName: Localize("START_DATE", lang), minWidth: 152,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "end_date", headerName: Localize("END_DATE", lang), minWidth: 152,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "declared_at", headerName: Localize("WORK_STOPPAGE_DECLARED_AT", lang), minWidth: 168,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "stoppage_status", headerName: Localize("WORK_STOPPAGE_STATUS", lang), minWidth: 152,
      sortable: true, filterable: true,
      filterOperators: [singleSelectFilterOperator([
        { value: "INITIAL", label: Localize("WORK_STOPPAGE_INITIAL", lang) },
        { value: "PROLONGATION", label: Localize("WORK_STOPPAGE_PROLONGATION", lang) },
      ])],
      renderCell: (params) => <StatusChip status={params.value === "PROLONGATION" ? "PROLONGATION" : "INITIAL"} />,
    },
    { field: "carence_days", headerName: Localize("WORK_STOPPAGE_CARENCE_DAYS", lang), minWidth: 136, type: "number", sortable: true, filterable: true },
    {
      field: "cpam_days", headerName: Localize("WORK_STOPPAGE_CPAM_DAYS", lang), minWidth: 176,
      type: "number", sortable: true, filterable: true,
      // A manually entered figure is marked, because the whole point of the override is that
      // it is NOT what the formula would say — someone reading the column has to be able to
      // tell the two apart without opening the row.
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{params.value === null || typeof params.value === "undefined" ? "" : params.value}</span>
          {params.row.cpam_days_overridden ? (
            <Tooltip title={Localize("WORK_STOPPAGE_MANUAL", lang)}>
              <Chip size="small" label="M" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }} />
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
    { field: "absence_days", headerName: Localize("WORK_STOPPAGE_ABSENCE_DAYS", lang), minWidth: 184, type: "number", sortable: true, filterable: true },
    // Maintien de salaire employeur — the Syntec incapacité schedule. Three columns rather
    // than one "90 jours" figure, because that is what payroll actually needs: the days at
    // full pay, the days at the reduced rate, and the days nobody maintains.
    { field: "maintenance_category", headerName: Localize("WORK_STOPPAGE_MAINTENANCE_CATEGORY", lang), minWidth: 144, sortable: true, filterable: true },
    {
      field: "maintenance_days_100", headerName: Localize("WORK_STOPPAGE_MAINTENANCE_100", lang), minWidth: 144,
      type: "number", sortable: true, filterable: true,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{params.value === null || typeof params.value === "undefined" ? "" : params.value}</span>
          {params.row.maintenance_overridden ? (
            <Tooltip title={Localize("WORK_STOPPAGE_MANUAL", lang)}>
              <Chip size="small" label="M" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }} />
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
    {
      field: "maintenance_days_80", headerName: Localize("WORK_STOPPAGE_MAINTENANCE_REDUCED", lang), minWidth: 176,
      type: "number", sortable: true, filterable: true,
      // The rate is a property of the convention, so it labels the value rather than
      // occupying a column of its own.
      renderCell: (params) => (params.value === null || typeof params.value === "undefined"
        ? ""
        : `${params.value}${params.row.maintenance_partial_rate ? ` @ ${Number(params.row.maintenance_partial_rate)}%` : ""}`),
    },
    { field: "maintenance_days_unpaid", headerName: Localize("WORK_STOPPAGE_MAINTENANCE_UNPAID", lang), minWidth: 168, type: "number", sortable: true, filterable: true },
    {
      field: "cpam_total", headerName: Localize("WORK_STOPPAGE_CPAM_TOTAL", lang), minWidth: 144,
      type: "number", sortable: true, filterable: true,
      renderCell: (params) => formatAmount(params.value),
    },
    {
      field: "prevoyance_total", headerName: Localize("WORK_STOPPAGE_PREVOYANCE_TOTAL", lang), minWidth: 168,
      type: "number", sortable: true, filterable: true,
      renderCell: (params) => formatAmount(params.value),
    },
    reimbursementColumn("cpam_reimbursement_status", "WORK_STOPPAGE_CPAM_REIMBURSEMENT_STATUS"),
    reimbursementColumn("prevoyance_reimbursement_status", "WORK_STOPPAGE_PREVOYANCE_REIMBURSEMENT_STATUS"),
    {
      field: "therapeutic_rate", headerName: Localize("WORK_STOPPAGE_THERAPEUTIC_RATE", lang), minWidth: 176,
      type: "number", sortable: true, filterable: true,
      renderCell: (params) => (params.value === null || typeof params.value === "undefined" ? "" : `${Number(params.value)} %`),
    },
    { field: "comments", headerName: Localize("Comments", lang), minWidth: 200, sortable: true, filterable: true },
    {
      field: "actions", headerName: "", width: canEdit ? 172 : 100, sortable: false, filterable: false,
      disableColumnMenu: true, disableReorder: true,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ height: "100%" }}>
          <Tooltip title={Localize("VIEW_DETAILS", lang)}>
            <IconButton size="small" onClick={() => onViewDetails(params.row)}>
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* The arrêt IS a planning leave, so its first day in the planning is where to
              check it against the schedule. */}
          <Tooltip title={Localize("WORK_STOPPAGE_OPEN_PLANNING", lang)}>
            <span>
              <IconButton size="small" disabled={!params.row.start_date} onClick={() => onOpenPlanning(params.row)}>
                <EventNoteRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {canEdit && (
            <>
              <Tooltip title={Localize("WORK_STOPPAGE_EDIT", lang)}>
                <IconButton size="small" onClick={() => onEdit(params.row)}>
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={Localize("Delete", lang)}>
                <IconButton size="small" color="error" onClick={() => onDelete(params.row)}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      ),
    },
  ];
  }, [lang, canEdit, onViewDetails, onOpenPlanning, onEdit, onDelete]);

  return (
    // Inline, so it also beats the class's per-breakpoint margin-top (80 / 100 / 140px).
    <div className={styles.collabsCard} style={compact ? { marginTop: 0, padding: "0.75rem 1rem" } : undefined}>
      <div className="p-2 mb-1 d-flex align-items-center" style={{ gap: 16 }}>
        <input
          type="text"
          className="form-control"
          placeholder={Localize("Search") + "..."}
          value={globalSearch}
          onChange={(e) => {
            const val = e.target.value;
            setGlobalSearch(val);
            setFilterModel((prev) => ({
              ...prev,
              quickFilterValues: val ? val.split(" ") : [],
            }));
          }}
          style={{ maxWidth: 350 }}
        />
        {/* Server-side, unlike the search box: "no CPAM payment yet" is the relance list, and
            it has to be answered over every arrêt rather than over the rows already loaded. */}
        <FormControlLabel
          control={<Switch size="small" checked={!!onlyUnpaid} onChange={(e) => onOnlyUnpaidChange(e.target.checked)} />}
          label={Localize("WORK_STOPPAGE_ONLY_UNPAID", lang)}
          sx={{ mb: 0, whiteSpace: "nowrap" }}
        />
        {/* Also server-side: resolved from the session, so it can only ever mean the caller. */}
        {onOnlyMineChange && (
          <FormControlLabel
            control={<Switch size="small" checked={!!onlyMine} onChange={(e) => onOnlyMineChange(e.target.checked)} />}
            label={Localize("WORK_STOPPAGE_ONLY_MINE", lang)}
            sx={{ mb: 0, whiteSpace: "nowrap" }}
          />
        )}
      </div>
      <WorkStoppagePresetFilters
        rows={data}
        value={preset}
        onChange={(value) => {
          setPreset(value);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        sx={{ px: 1, pb: 1 }}
      />
      <div style={{ height: scrollHeight, width: "100%" }}>
        <DataGrid
          localeText={dataGridLocale.components.MuiDataGrid.defaultProps.localeText}
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          filterModel={filterModel}
          onFilterModelChange={(newModel) => {
            setFilterModel((prev) => ({
              ...newModel,
              quickFilterValues: prev.quickFilterValues,
            }));
          }}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
          slots={{ noRowsOverlay: NoRowsOverlay }}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": { display: "flex", alignItems: "center", py: 1 },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e0e0e0",
              minHeight: 52,
            },
            "& .MuiTablePagination-root": {
              width: "100%",
              overflow: "hidden",
            },
            "& .MuiTablePagination-toolbar": {
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: 52,
              px: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              margin: 0,
            },
            '& .MuiDataGrid-cell[data-field="actions"]': {
              backgroundColor: "var(--surface-card, #fff)",
            },
          }}
        />
      </div>
    </div>
  );
}
